/**
 * The shape of each video on the native call stage.
 *
 * Robert, 2026-10-05, late: "all video should fill their respective portions
 * of the screen (unless they zoom out)". So the cards no longer take the
 * shape of their video: every picture fills its part of the screen (cover),
 * and a fit button shows the whole picture. VRSLayout.native.tsx keeps the
 * last shape that each renderer reported, also while a picture pauses, for
 * two things:
 *
 * - the grid plan, which picks the cells closest to the remote videos'
 *   shape (stageGrid.ts)
 * - the floating tile, which has the shape of the picture in it (fitToSlot).
 *
 * Pure helpers here.
 */
/**
 * A number that a size or a shape can be.
 *
 * @param {number} value - The number to check.
 * @returns {boolean}
 */
function isPositive(value) {
    return typeof value === 'number' && Number.isFinite(value) && value > 0;
}
/**
 * The largest box of the given shape (width / height) that fits a slot (the
 * floating tile: its long side in a square slot).
 *
 * @param {number} slotWidth - The width of the slot.
 * @param {number} slotHeight - The height of the slot.
 * @param {number} aspect - The width of the video divided by its height.
 * @returns {IVideoSize|undefined} The box size, or undefined while the slot
 * or the shape is not known (the caller then uses its default size).
 */
export function fitToSlot(slotWidth, slotHeight, aspect) {
    if (!isPositive(slotWidth) || !isPositive(slotHeight) || !isPositive(aspect)) {
        return undefined;
    }
    const width = Math.min(slotWidth, slotHeight * aspect);
    return {
        height: width / aspect,
        width
    };
}
/**
 * The shape of the picture as the person sees it.
 *
 * The iOS renderer reports the size of the picture as it is drawn. The
 * Android renderer (WebRTCView in react-native-webrtc 124) reports the frame
 * before it turns it, and does not say how far it turns it. A phone camera
 * turns with the screen, so on Android your own video takes its long side
 * along the screen's long side. The video of another person on Android has no
 * known shape: the grid plan then uses the shape of a camera held the same
 * way as the screen.
 *
 * @param {Object} options - The reported size and where it shows.
 * @returns {number|undefined} The width divided by the height. Undefined if
 * the shape is not known.
 */
export function shownVideoAspect({ height, landscape, local, platform, width }) {
    if (!isPositive(width) || !isPositive(height)) {
        return undefined;
    }
    if (platform !== 'android') {
        return width / height;
    }
    if (!local) {
        return undefined;
    }
    const long = Math.max(width, height);
    const short = Math.min(width, height);
    return landscape ? long / short : short / long;
}
